import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Stack, Typography, Select, MenuItem, Checkbox, FormControlLabel, TextField, IconButton } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { getWorkoutProgram, getExercises } from '../../utils/api';
import ProgramExerciseItem from './ProgramExerciseItem';
import Loader from '../Loader.js';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';

const StartWorkout = ({ userId }) => {
    const { programId } = useParams();
    const [programData, setProgramData] = useState(null);
    const [allExercises, setAllExercises] = useState([]);
    const [selectedDay, setSelectedDay] = useState('');
    const [exercises, setExercises] = useState([]);
    const [completedExercises, setCompletedExercises] = useState([]);
    const [timerDurations, setTimerDurations] = useState([]);
    const [timerStates, setTimerStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFirstPopup, setShowFirstPopup] = useState(false); // Popup for first exercise
    const [showHalfwayPopup, setShowHalfwayPopup] = useState(false); // Popup for halfway point
    const [halfwayPoint, setHalfwayPoint] = useState(0); // To store halfway point

    // Save state to localStorage
    const saveStateToLocalStorage = (day) => {
        if (!day) return;
        const stateToSave = {
            completedExercises,
            timerDurations,
            timerStates: timerStates.map(ts => ({ remainingTime: ts.remainingTime, isRunning: ts.isRunning })),
        };
        localStorage.setItem(`workoutState_${programId}_${day}`, JSON.stringify(stateToSave));
    };

    // Load state from localStorage
    const loadStateForDay = (day) => {
        const savedState = localStorage.getItem(`workoutState_${programId}_${day}`);
        if (savedState) {
            const parsedState = JSON.parse(savedState);
            const numExercises = exercises.length;
            setCompletedExercises(
                Array.from({ length: numExercises }, (_, i) => parsedState.completedExercises?.[i] || false)
            );
            setTimerDurations(
                Array.from({ length: numExercises }, (_, i) => parsedState.timerDurations?.[i] || { minutes: 0, seconds: 0 })
            );
            setTimerStates(
                Array.from({ length: numExercises }, (_, i) => parsedState.timerStates?.[i] || { remainingTime: 0, isRunning: false })
            );
        } else {
            setCompletedExercises(new Array(exercises.length).fill(false));
            setTimerDurations(exercises.map(() => ({ minutes: 0, seconds: 0 })));
            setTimerStates(exercises.map(() => ({ remainingTime: 0, isRunning: false })));
        }
    };

    // Fetch initial data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [exerciseData, programData] = await Promise.all([
                    getExercises(),
                    getWorkoutProgram(programId),
                ]);
                setAllExercises(exerciseData);
                setProgramData(programData);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };
        fetchData();
    }, [programId]);

    // Update exercises and load state when a day is selected
    useEffect(() => {
        if (programData && selectedDay && allExercises.length > 0) {
            const dayExercises = programData.exercises
                .filter(ex => ex.day === selectedDay)
                .map(ex => {
                    const exercise = allExercises.find(e => e._id.toString() === ex.exerciseId.toString());
                    return {
                        ...ex,
                        exercise: exercise || { name: ex.exerciseName },
                    };
                });
            setExercises(dayExercises);
            loadStateForDay(selectedDay);
            setHalfwayPoint(Math.ceil(dayExercises.length / 2)); // Calculate halfway point
        }
    }, [programData, selectedDay, allExercises]);

    // Save state when it changes
    useEffect(() => {
        if (selectedDay) {
            saveStateToLocalStorage(selectedDay);
        }
    }, [completedExercises, timerDurations, timerStates, selectedDay]);

    // Timer interval to update running timers
    useEffect(() => {
        const intervalId = setInterval(() => {
            setTimerStates(prev =>
                prev.map(ts => {
                    if (ts.isRunning && ts.remainingTime > 0) {
                        return { ...ts, remainingTime: ts.remainingTime - 1 };
                    } else if (ts.isRunning && ts.remainingTime <= 0) {
                        return { ...ts, isRunning: false };
                    }
                    return ts;
                })
            );
        }, 1000);
        return () => clearInterval(intervalId);
    }, []);

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const k = completedExercises.findIndex(completed => !completed);
        const numChecked = k === -1 ? exercises.length : k;
        if (result.destination.index < numChecked) {
            return; // Prevent dropping above checked exercises
        }
        const reorderedExercises = Array.from(exercises);
        const [movedExercise] = reorderedExercises.splice(result.source.index, 1);
        reorderedExercises.splice(result.destination.index, 0, movedExercise);
        setExercises(reorderedExercises);

        const newCompleted = Array.from(completedExercises);
        const [movedCompleted] = newCompleted.splice(result.source.index, 1);
        newCompleted.splice(result.destination.index, 0, movedCompleted);
        setCompletedExercises(newCompleted);

        const newTimerDurations = Array.from(timerDurations);
        const [movedDuration] = newTimerDurations.splice(result.source.index, 1);
        newTimerDurations.splice(result.destination.index, 0, movedDuration);
        setTimerDurations(newTimerDurations);

        const newTimerStates = Array.from(timerStates);
        const [movedState] = newTimerStates.splice(result.source.index, 1);
        newTimerStates.splice(result.destination.index, 0, movedState);
        setTimerStates(newTimerStates);
    };

    const handleCheckboxChange = (index) => {
        const newCompleted = [...completedExercises];
        const isChecking = !newCompleted[index];
        newCompleted[index] = isChecking;
        if (!isChecking) {
            for (let i = index + 1; i < newCompleted.length; i++) {
                newCompleted[i] = false;
            }
        }
        setCompletedExercises(newCompleted);

        // Check for first exercise completion
        if (index === 0 && isChecking) {
            setShowFirstPopup(true);
        }

        // Check for halfway point
        const completedCount = newCompleted.filter(completed => completed).length;
        if (completedCount === halfwayPoint) {
            setShowHalfwayPopup(true);
        }
    };

    const handleMinutesChange = (index, value) => {
        const newTimerDurations = [...timerDurations];
        newTimerDurations[index] = { ...newTimerDurations[index], minutes: parseInt(value) || 0 };
        setTimerDurations(newTimerDurations);
        const duration = (newTimerDurations[index].minutes * 60) + (newTimerDurations[index].seconds || 0);
        setTimerStates(prev => {
            const updated = [...prev];
            updated[index] = { remainingTime: duration, isRunning: false };
            return updated;
        });
    };

    const handleSecondsChange = (index, value) => {
        const newTimerDurations = [...timerDurations];
        newTimerDurations[index] = { ...newTimerDurations[index], seconds: parseInt(value) || 0 };
        setTimerDurations(newTimerDurations);
        const duration = (newTimerDurations[index].minutes || 0) * 60 + newTimerDurations[index].seconds;
        setTimerStates(prev => {
            const updated = [...prev];
            updated[index] = { remainingTime: duration, isRunning: false };
            return updated;
        });
    };

    const startTimer = (index) => {
        if (timerStates[index].remainingTime > 0) {
            setTimerStates(prev => {
                const updated = [...prev];
                updated[index].isRunning = true;
                return updated;
            });
        } else {
            const duration = (timerDurations[index].minutes * 60) + timerDurations[index].seconds;
            if (duration > 0) {
                setTimerStates(prev => {
                    const updated = [...prev];
                    updated[index] = { remainingTime: duration, isRunning: true };
                    return updated;
                });
            }
        }
    };

    const pauseTimer = (index) => {
        setTimerStates(prev => {
            const updated = [...prev];
            updated[index].isRunning = false;
            return updated;
        });
    };

    const resetTimer = (index) => {
        const originalDuration = (timerDurations[index].minutes * 60) + timerDurations[index].seconds;
        setTimerStates(prev => {
            const updated = [...prev];
            updated[index] = { remainingTime: originalDuration, isRunning: false };
            return updated;
        });
    };

    if (loading) return <Loader />;

    const days = [...new Set(programData?.exercises.map(ex => ex.day) || [])];

    return (
        <Box sx={{ padding: '20px', direction: 'rtl', margin: '0 auto', textAlign: 'center', maxWidth: '400px' }}>
            <Typography variant="h4" sx={{ color: '#8B4513', fontFamily: 'GveretLevin' }}>
                התחלת אימון
            </Typography>
            <Select
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                displayEmpty
                sx={{ direction: 'rtl', textAlign: 'right', mt: 2 }}
            >
                <MenuItem value="" disabled>בחר יום</MenuItem>
                {days.map((day, index) => (
                    <MenuItem key={index} value={day}>{day}</MenuItem>
                ))}
            </Select>
            {selectedDay && exercises.length > 0 ? (
                <>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="exercises">
                            {(provided) => (
                                <Stack {...provided.droppableProps} ref={provided.innerRef} sx={{ mt: 2 }}>
                                    {exercises.map((ex, index) => {
                                        const isLocked = index > 0 && !completedExercises[index - 1];
                                        const durationSet = (timerDurations[index]?.minutes || 0) > 0 || (timerDurations[index]?.seconds || 0) > 0;
                                        return (
                                            <Draggable
                                                key={ex._id}
                                                draggableId={ex._id}
                                                index={index}
                                                isDragDisabled={completedExercises[index]}
                                            >
                                                {(provided) => (
                                                    <Box
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        sx={{
                                                            mb: 2,
                                                            p: 2,
                                                            border: '1px solid #ccc',
                                                            borderRadius: '5px',
                                                            backgroundColor: isLocked ? '#f0f0f0' : '#fff',
                                                        }}
                                                    >
                                                        <ProgramExerciseItem
                                                            exercise={ex.exercise}
                                                            sets={ex.sets}
                                                            reps={ex.reps}
                                                            weight={ex.weight}
                                                            editable={false}
                                                        />
                                                        <Stack direction="row" alignItems="center" sx={{ position: 'relative', width: '100%' }}>
                                                            <Stack direction="row" justifyContent="center" sx={{ flexGrow: 1 }}>
                                                                <TextField
                                                                    label="דקות"
                                                                    type="number"
                                                                    value={timerDurations[index]?.minutes ?? 0}
                                                                    onChange={(e) => handleMinutesChange(index, e.target.value)}
                                                                    sx={{ width: '60px' }}
                                                                    InputProps={{ inputProps: { min: 0 } }}
                                                                    disabled={isLocked}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                                <TextField
                                                                    label="שניות"
                                                                    type="number"
                                                                    value={timerDurations[index]?.seconds ?? 0}
                                                                    onChange={(e) => handleSecondsChange(index, e.target.value)}
                                                                    sx={{ width: '60px' }}
                                                                    InputProps={{ inputProps: { min: 0, max: 59 } }}
                                                                    disabled={isLocked}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            </Stack>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={completedExercises[index]}
                                                                        onChange={() => handleCheckboxChange(index)}
                                                                        disabled={isLocked}
                                                                    />
                                                                }
                                                                sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}
                                                            />
                                                        </Stack>
                                                        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                                                            <IconButton
                                                                onClick={() => startTimer(index)}
                                                                disabled={isLocked || timerStates[index]?.isRunning || !durationSet}
                                                            >
                                                                <PlayArrowIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                onClick={() => pauseTimer(index)}
                                                                disabled={isLocked || !timerStates[index]?.isRunning}
                                                            >
                                                                <PauseIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                onClick={() => resetTimer(index)}
                                                                disabled={isLocked || (timerStates[index]?.remainingTime === 0 && !timerStates[index]?.isRunning)}
                                                            >
                                                                <ReplayIcon />
                                                            </IconButton>
                                                        </Stack>
                                                        <Box mt="10px">
                                                            {timerStates[index]?.remainingTime > 0 && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {Math.floor(timerStates[index].remainingTime / 60)}:
                                                                    {String(timerStates[index].remainingTime % 60).padStart(2, '0')}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </Stack>
                            )}
                        </Droppable>
                    </DragDropContext>
                    {/* First Exercise Popup */}
                    {showFirstPopup && (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1000,
                            }}
                            onClick={() => setShowFirstPopup(false)}
                        >
                            <div
                                style={{
                                    backgroundColor: '#FFF',
                                    padding: '40px',
                                    borderRadius: '10px',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                    maxWidth: '400px',
                                    width: '70%',
                                    zIndex: 1001,
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <p style={{ fontSize: '24px', fontWeight: '700', textAlign: 'center', marginBottom: '20px' }}>
                                    התחלת טוב! המשך כך!
                                </p>
                                <button
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#B5A78A',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onClick={() => setShowFirstPopup(false)}
                                >
                                    <span style={{ fontSize: '20px', marginRight: '5px' }}>
                                        המשך!
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}
                    {/* Halfway Popup */}
                    {showHalfwayPopup && (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1000,
                            }}
                            onClick={() => setShowHalfwayPopup(false)}
                        >
                            <div
                                style={{
                                    backgroundColor: '#FFF',
                                    padding: '40px',
                                    borderRadius: '10px',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                    maxWidth: '400px',
                                    width: '70%',
                                    zIndex: 1001,
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <p style={{ fontSize: '24px', fontWeight: '700', textAlign: 'center', marginBottom: '20px' }}>
                                    חצי הדרך! עוד קצת וסיימנו!
                                </p>
                                <button
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#B5A78A',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onClick={() => setShowHalfwayPopup(false)}
                                >
                                    <span style={{ fontSize: '20px', marginRight: '5px' }}>
                                        המשך!
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}
                </>
            ) : selectedDay ? (
                <Typography sx={{ mt: 2 }}>אין תרגילים ליום זה</Typography>
            ) : null}
        </Box>
    );
};

export default StartWorkout;